import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, Edit, Trash2, BarChart3, Eye, MousePointerClick, Loader2 } from "lucide-react";
import { Advertisement, AdLocation, AdPlan, AdStatus } from "@/types/advertisement";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { signOut } from "@/lib/firebase";
import { australianStates } from "@shared/regions";
import { AdStatisticsChart } from "@/components/AdStatisticsChart";
import { AdAnalyticsDashboard } from "@/components/AdAnalyticsDashboard";
import { AdInvoiceGenerator } from "@/components/AdInvoiceGenerator";
import { AddressAutocomplete, type AddressDetails } from "@/components/AddressAutocomplete";

export default function AdminAds() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState<Advertisement | null>(null);
  
  // Filtering and pagination for List tab
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Filtering and pagination for Invoices tab
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>("all");
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const invoiceItemsPerPage = 10;

  const [formData, setFormData] = useState({
    advertiser: "",
    contact: "",
    email: "",
    businessAddress: "",
    businessCity: "",
    businessState: "",
    businessPostcode: "",
    imageUrl: "",
    mobileImageUrl: "",
    link: "",
    startDate: "",
    endDate: "",
    location: "homepage_top" as AdLocation,
    region: "ALL" as string,
    plan: "fixed" as AdPlan,
    price: 0,
    tags: [] as string[],
    priority: 5,
    monthlyLimit: 0, // 0 means unlimited
  });
  
  const [tagsInput, setTagsInput] = useState("");

  useEffect(() => {
    if (user && isAdmin) {
      fetchAds();
    }
  }, [user, isAdmin]);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/ads");
      const data = await response.json();
      // Ensure data is an array
      setAds(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching ads:", error);
      setAds([]); // Reset to empty array on error
      toast({
        title: "오류",
        description: "광고 목록을 불러오는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Convert tagsInput to tags array
      let tags = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
      
      // 지역이 선택되었을 때 자동으로 지역 태그 추가
      if (formData.region && formData.region !== "ALL") {
        if (!tags.includes(formData.region)) {
          tags = [...tags, formData.region];
        }
      }
      
      const dataToSubmit = { ...formData, tags };
      
      if (editingAd) {
        // Update existing ad
        await apiRequest("PUT", `/api/ads/${editingAd.id}`, dataToSubmit);
        toast({ title: "성공", description: "광고가 수정되었습니다." });
      } else {
        // Create new ad
        await apiRequest("POST", "/api/ads", dataToSubmit);
        toast({ title: "성공", description: "새 광고가 등록되었습니다." });
      }

      setIsDialogOpen(false);
      setEditingAd(null);
      resetForm();
      fetchAds();
    } catch (error) {
      console.error("Error saving ad:", error);
      toast({
        title: "오류",
        description: "광고 저장 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("정말 이 광고를 삭제하시겠습니까?")) return;

    try {
      await apiRequest("DELETE", `/api/ads/${id}`, {});
      toast({ title: "성공", description: "광고가 삭제되었습니다." });
      fetchAds();
    } catch (error) {
      console.error("Error deleting ad:", error);
      toast({
        title: "오류",
        description: "광고 삭제 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (ad: Advertisement) => {
    setEditingAd(ad);
    setFormData({
      advertiser: ad.advertiser,
      contact: ad.contact,
      email: ad.email || "",
      businessAddress: ad.businessAddress || "",
      businessCity: ad.businessCity || "",
      businessState: ad.businessState || "",
      businessPostcode: ad.businessPostcode || "",
      imageUrl: ad.imageUrl,
      mobileImageUrl: ad.mobileImageUrl || "",
      link: ad.link,
      startDate: ad.startDate,
      endDate: ad.endDate,
      location: ad.location,
      region: ad.region || "ALL",
      plan: ad.plan,
      price: Number(ad.price || "0"),
      tags: ad.tags || [],
      priority: Number(ad.priority || "5"),
      monthlyLimit: Number(ad.monthlyLimit || "0"),
    });
    setTagsInput((ad.tags || []).join(", "));
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      advertiser: "",
      contact: "",
      email: "",
      businessAddress: "",
      businessCity: "",
      businessState: "",
      businessPostcode: "",
      imageUrl: "",
      mobileImageUrl: "",
      link: "",
      startDate: "",
      endDate: "",
      location: "homepage_top",
      region: "ALL",
      plan: "fixed",
      price: 0,
      tags: [],
      priority: 5,
      monthlyLimit: 0,
    });
    setTagsInput("");
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingAd(null);
      resetForm();
    }
  };

  const getLocationLabel = (location: AdLocation) => {
    const labels: Record<AdLocation, string> = {
      homepage_top: "홈페이지 상단",
      sidebar: "사이드바",
      article_top: "기사 상단",
      article_bottom: "기사 하단",
      mobile_bottom: "모바일 하단",
    };
    return labels[location];
  };

  const getPlanLabel = (plan: AdPlan) => {
    const labels: Record<AdPlan, string> = {
      fixed: "정액제",
      cpc: "클릭당 과금",
      cpm: "노출당 과금",
    };
    return labels[plan];
  };

  const getStatusColor = (status: AdStatus) => {
    const colors: Record<AdStatus, string> = {
      active: "bg-green-500",
      paused: "bg-yellow-500",
      expired: "bg-red-500",
    };
    return colors[status];
  };

  const calculateCTR = (clicks: number, views: number) => {
    if (views === 0) return 0;
    return ((clicks / views) * 100).toFixed(2);
  };

  // Get unique advertisers for autocomplete
  const uniqueAdvertisers = Array.from(new Set(ads.map(ad => ad.advertiser))).sort();

  // Filter and paginate ads for List tab
  const filteredAds = ads.filter(ad => {
    const matchesSearch = ad.advertiser.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ad.contact.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || ad.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredAds.length / itemsPerPage);
  const paginatedAds = filteredAds.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);
  
  // Filter and paginate ads for Invoices tab
  const filteredInvoiceAds = ads.filter(ad => {
    const matchesSearch = ad.advertiser.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                          ad.contact.toLowerCase().includes(invoiceSearchTerm.toLowerCase());
    const matchesStatus = invoiceStatusFilter === "all" || ad.status === invoiceStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalInvoicePages = Math.ceil(filteredInvoiceAds.length / invoiceItemsPerPage);
  const paginatedInvoiceAds = filteredInvoiceAds.slice(
    (invoiceCurrentPage - 1) * invoiceItemsPerPage,
    invoiceCurrentPage * invoiceItemsPerPage
  );

  // Reset to page 1 when invoice filters change
  useEffect(() => {
    setInvoiceCurrentPage(1);
  }, [invoiceSearchTerm, invoiceStatusFilter]);

  // Get recommended image sizes for each ad location
  const getRecommendedSizes = (location: AdLocation) => {
    const sizes: Record<AdLocation, { pc: string; mobile: string; description: string }> = {
      homepage_top: {
        pc: "728x90 또는 970x90",
        mobile: "320x50 또는 320x100",
        description: "홈페이지 상단 배너 (리더보드)"
      },
      sidebar: {
        pc: "300x250 또는 160x600",
        mobile: "300x250",
        description: "사이드바 광고"
      },
      article_top: {
        pc: "728x90 또는 468x60",
        mobile: "320x50",
        description: "기사 상단 배너"
      },
      article_bottom: {
        pc: "728x90 또는 300x250",
        mobile: "320x50 또는 300x250",
        description: "기사 하단 배너"
      },
      mobile_bottom: {
        pc: "728x90",
        mobile: "320x50 또는 320x100",
        description: "모바일 하단 고정 배너"
      },
    };
    return sizes[location];
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <h2 className="text-xl font-semibold mb-2">로그인 필요</h2>
            <p className="text-muted-foreground text-center mb-6">
              이 페이지는 로그인이 필요합니다.
            </p>
            <Link href="/admin">
              <Button>관리자 로그인</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin access check
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>접근 권한 없음</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              이 페이지는 관리자만 접근할 수 있습니다.
            </p>
            <div className="flex gap-2">
              <Link href="/">
                <Button data-testid="button-go-home">홈으로 이동</Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => signOut()}
                data-testid="button-logout"
              >
                로그아웃
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">광고 로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link href="/admin">
          <div>
            <Button variant="ghost" className="mb-6" data-testid="button-back">
              <ArrowLeft className="h-4 w-4 mr-2" />
              관리자 페이지
            </Button>
          </div>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">광고 관리</h1>
            <p className="text-muted-foreground">
              직접 광고를 등록하고 통계를 확인하세요
            </p>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button data-testid="button-new-ad">
                <Plus className="h-4 w-4 mr-2" />
                새 광고 등록
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingAd ? "광고 수정" : "새 광고 등록"}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="advertiser">광고주 이름</Label>
                  <Input
                    id="advertiser"
                    list="advertiser-list"
                    value={formData.advertiser}
                    onChange={(e) => {
                      const selectedAdvertiser = e.target.value;
                      setFormData({ ...formData, advertiser: selectedAdvertiser });
                      
                      // Auto-fill all fields if advertiser exists
                      const existingAd = ads.find(ad => ad.advertiser === selectedAdvertiser);
                      if (existingAd && !editingAd) {
                        setFormData(prev => ({
                          ...prev,
                          advertiser: selectedAdvertiser,
                          contact: existingAd.contact,
                          email: existingAd.email || "",
                          businessAddress: existingAd.businessAddress || "",
                          businessCity: existingAd.businessCity || "",
                          businessState: existingAd.businessState || "",
                          businessPostcode: existingAd.businessPostcode || "",
                        }));
                      }
                    }}
                    required
                    data-testid="input-advertiser"
                    placeholder="기존 업체를 검색하거나 새 업체명을 입력하세요"
                  />
                  <datalist id="advertiser-list">
                    {uniqueAdvertisers.map((advertiser, index) => (
                      <option key={index} value={advertiser} />
                    ))}
                  </datalist>
                  {uniqueAdvertisers.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      기존 등록된 {uniqueAdvertisers.length}개 업체 중 선택 가능 (자동입력)
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact">전화</Label>
                    <Input
                      id="contact"
                      type="tel"
                      value={formData.contact}
                      onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                      placeholder="예: 02-1234-5678"
                      required
                      data-testid="input-contact"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">이메일</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="예: contact@company.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <Label>사업장 주소</Label>
                  <div className="space-y-3">
                    <AddressAutocomplete
                      value={formData.businessAddress}
                      onChange={(address) => setFormData({ ...formData, businessAddress: address })}
                      onDetailsChange={(details: AddressDetails) => {
                        setFormData({
                          ...formData,
                          businessAddress: details.address || details.fullAddress,
                          businessCity: details.city,
                          businessState: details.state,
                          businessPostcode: details.postcode,
                        });
                      }}
                      label=""
                      placeholder="주소를 입력하세요 (예: 123 Main St, Sydney NSW)"
                    />
                    
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        id="businessCity"
                        value={formData.businessCity}
                        onChange={(e) => setFormData({ ...formData, businessCity: e.target.value })}
                        placeholder="도시 (예: Sydney)"
                        data-testid="input-business-city"
                      />
                      
                      <Select
                        value={formData.businessState}
                        onValueChange={(value) => setFormData({ ...formData, businessState: value })}
                      >
                        <SelectTrigger data-testid="select-business-state">
                          <SelectValue placeholder="주/구역" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NSW">NSW</SelectItem>
                          <SelectItem value="VIC">VIC</SelectItem>
                          <SelectItem value="QLD">QLD</SelectItem>
                          <SelectItem value="SA">SA</SelectItem>
                          <SelectItem value="WA">WA</SelectItem>
                          <SelectItem value="TAS">TAS</SelectItem>
                          <SelectItem value="NT">NT</SelectItem>
                          <SelectItem value="ACT">ACT</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Input
                        id="businessPostcode"
                        value={formData.businessPostcode}
                        onChange={(e) => setFormData({ ...formData, businessPostcode: e.target.value })}
                        placeholder="우편번호 (예: 2000)"
                        maxLength={4}
                        data-testid="input-business-postcode"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    사업장 주소는 선택사항입니다 (지역 타겟팅에 활용)
                  </p>
                </div>

                {/* Show recommended sizes for selected location */}
                {formData.location && (
                  <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                    <CardContent className="py-3">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                        {getRecommendedSizes(formData.location).description}
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs text-blue-700 dark:text-blue-300">
                        <div>
                          <span className="font-semibold">PC 권장:</span> {getRecommendedSizes(formData.location).pc}
                        </div>
                        <div>
                          <span className="font-semibold">모바일 권장:</span> {getRecommendedSizes(formData.location).mobile}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div>
                  <Label htmlFor="imageUrl">PC 이미지 URL</Label>
                  <Input
                    id="imageUrl"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder={`https://example.com/banner-pc.jpg ${formData.location ? `(권장: ${getRecommendedSizes(formData.location).pc})` : ''}`}
                    required
                    data-testid="input-image-url"
                  />
                </div>

                <div>
                  <Label htmlFor="mobileImageUrl">모바일 이미지 URL (선택)</Label>
                  <Input
                    id="mobileImageUrl"
                    value={formData.mobileImageUrl}
                    onChange={(e) => setFormData({ ...formData, mobileImageUrl: e.target.value })}
                    placeholder={`https://example.com/banner-mobile.jpg ${formData.location ? `(권장: ${getRecommendedSizes(formData.location).mobile})` : ''}`}
                    data-testid="input-mobile-image-url"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    미입력 시 PC 이미지가 자동 조정됩니다
                  </p>
                </div>

                <div>
                  <Label htmlFor="link">링크 URL</Label>
                  <Input
                    id="link"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://example.com"
                    required
                    data-testid="input-link"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">시작일</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      required
                      data-testid="input-start-date"
                    />
                  </div>

                  <div>
                    <Label htmlFor="endDate">종료일</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      required
                      data-testid="input-end-date"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="location">광고 위치</Label>
                    <Select
                      value={formData.location}
                      onValueChange={(value) => setFormData({ ...formData, location: value as AdLocation })}
                    >
                      <SelectTrigger data-testid="select-location">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="homepage_top">홈페이지 상단</SelectItem>
                        <SelectItem value="sidebar">사이드바</SelectItem>
                        <SelectItem value="article_top">기사 상단</SelectItem>
                        <SelectItem value="article_bottom">기사 하단</SelectItem>
                        <SelectItem value="mobile_bottom">모바일 하단</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="region">지역 타겟팅</Label>
                    <Select
                      value={formData.region}
                      onValueChange={(value) => setFormData({ ...formData, region: value })}
                    >
                      <SelectTrigger data-testid="select-region">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {australianStates.map((state) => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.nameKo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      특정 지역에만 광고 표시
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="plan">과금 방식</Label>
                    <Select
                      value={formData.plan}
                      onValueChange={(value) => setFormData({ ...formData, plan: value as AdPlan })}
                    >
                      <SelectTrigger data-testid="select-plan">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">정액제</SelectItem>
                        <SelectItem value="cpc">클릭당 과금 (CPC)</SelectItem>
                        <SelectItem value="cpm">노출당 과금 (CPM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="price">가격 (AUD)</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                    required
                    data-testid="input-price"
                  />
                </div>

                <div>
                  <Label htmlFor="tags">타겟 태그 (쉼표로 구분)</Label>
                  <Input
                    id="tags"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    placeholder="호주, 부동산, 투자"
                    data-testid="input-tags"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    기사 태그와 일치하는 광고가 우선 표시됩니다
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="priority">우선순위 (1-10)</Label>
                    <Input
                      id="priority"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
                      required
                      data-testid="input-priority"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      높을수록 더 자주 표시됩니다
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="monthlyLimit">월 노출 한도</Label>
                    <Input
                      id="monthlyLimit"
                      type="number"
                      min="0"
                      value={formData.monthlyLimit}
                      onChange={(e) => setFormData({ ...formData, monthlyLimit: Number(e.target.value) })}
                      placeholder="0 (무제한)"
                      data-testid="input-monthly-limit"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      0 = 무제한
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                    취소
                  </Button>
                  <Button type="submit" data-testid="button-submit">
                    {editingAd ? "수정" : "등록"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs for different views */}
        <Tabs defaultValue="list" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="list" data-testid="tab-list">광고 목록</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">통계 & 분석</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">인보이스</TabsTrigger>
          </TabsList>

          {/* List Tab */}
          <TabsContent value="list">
            {/* Search and Filter */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <Input
                  placeholder="광고주 이름 또는 연락처로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="input-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]" data-testid="select-status-filter">
                  <SelectValue placeholder="상태 필터" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체 상태</SelectItem>
                  <SelectItem value="active">활성</SelectItem>
                  <SelectItem value="paused">일시정지</SelectItem>
                  <SelectItem value="expired">만료됨</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">총 광고</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{ads.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">총 노출</CardTitle>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {ads.reduce((sum, ad) => sum + Number(ad.views || "0"), 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">총 클릭</CardTitle>
                  <MousePointerClick className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {ads.reduce((sum, ad) => sum + Number(ad.clicks || "0"), 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">평균 CTR</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {calculateCTR(
                      ads.reduce((sum, ad) => sum + Number(ad.clicks || "0"), 0),
                      ads.reduce((sum, ad) => sum + Number(ad.views || "0"), 0)
                    )}%
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Ads Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>광고주</TableHead>
                      <TableHead>위치</TableHead>
                      <TableHead>기간</TableHead>
                      <TableHead>과금 방식</TableHead>
                      <TableHead>노출</TableHead>
                      <TableHead>클릭</TableHead>
                      <TableHead>CTR</TableHead>
                      <TableHead>상태</TableHead>
                      <TableHead>작업</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAds.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          {searchTerm || statusFilter !== "all" ? "검색 결과가 없습니다" : "등록된 광고가 없습니다"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedAds.map((ad) => (
                        <TableRow key={ad.id}>
                          <TableCell className="font-medium">{ad.advertiser}</TableCell>
                          <TableCell>{getLocationLabel(ad.location)}</TableCell>
                          <TableCell className="text-sm">
                            {ad.startDate} ~ {ad.endDate}
                          </TableCell>
                          <TableCell>{getPlanLabel(ad.plan)}</TableCell>
                          <TableCell>{Number(ad.views || "0").toLocaleString()}</TableCell>
                          <TableCell>{Number(ad.clicks || "0").toLocaleString()}</TableCell>
                          <TableCell>{calculateCTR(Number(ad.clicks || "0"), Number(ad.views || "0"))}%</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${getStatusColor(ad.status)} text-white border-0`}>
                              {ad.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEdit(ad)}
                                data-testid={`button-edit-${ad.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDelete(ad.id)}
                                data-testid={`button-delete-${ad.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    총 {filteredAds.length}개 중 {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredAds.length)}개 표시
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      data-testid="button-prev-page"
                    >
                      이전
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            data-testid={`button-page-${pageNum}`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      data-testid="button-next-page"
                    >
                      다음
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-8">
              <AdAnalyticsDashboard ads={ads} />
              <div className="border-t pt-8">
                <h2 className="text-2xl font-bold mb-6">기본 통계</h2>
                <AdStatisticsChart ads={ads} />
              </div>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <CardTitle>Invoice Generation</CardTitle>
                <p className="text-sm text-muted-foreground mt-2">
                  Generate professional invoices for your advertisers based on their campaign performance.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Search and Filter */}
                  <div className="flex gap-4 mb-4">
                    <div className="flex-1">
                      <Input
                        placeholder="광고주 이름 또는 연락처로 검색..."
                        value={invoiceSearchTerm}
                        onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                        data-testid="input-invoice-search"
                      />
                    </div>
                    <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                      <SelectTrigger className="w-[200px]" data-testid="select-invoice-status-filter">
                        <SelectValue placeholder="상태 필터" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">전체 상태</SelectItem>
                        <SelectItem value="active">활성</SelectItem>
                        <SelectItem value="paused">일시정지</SelectItem>
                        <SelectItem value="expired">만료됨</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {paginatedInvoiceAds.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {invoiceSearchTerm || invoiceStatusFilter !== "all" ? "검색 결과가 없습니다" : "등록된 광고가 없습니다"}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paginatedInvoiceAds.map((ad) => (
                        <div key={ad.id} className="flex items-center justify-between p-4 border rounded-lg hover-elevate">
                          <div className="flex-1">
                            <div className="font-medium">{ad.advertiser}</div>
                            <div className="text-sm text-muted-foreground">
                              {ad.startDate} to {ad.endDate} · {getPlanLabel(ad.plan)}
                              {ad.plan !== "fixed" && (
                                <>
                                  {" · "}
                                  {Number(ad.views || "0").toLocaleString()} views · 
                                  {Number(ad.clicks || "0").toLocaleString()} clicks
                                </>
                              )}
                              {ad.plan === "fixed" && " · 선불"}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right mr-4">
                              <div className="font-bold">AUD ${ad.price}</div>
                              <div className="text-xs text-muted-foreground">{ad.plan === "fixed" ? "선불 금액" : "Base Price"}</div>
                            </div>
                            <AdInvoiceGenerator ad={ad} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Pagination */}
                  {totalInvoicePages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t mt-4">
                      <div className="text-sm text-muted-foreground">
                        총 {filteredInvoiceAds.length}개 중 {(invoiceCurrentPage - 1) * invoiceItemsPerPage + 1}-{Math.min(invoiceCurrentPage * invoiceItemsPerPage, filteredInvoiceAds.length)}개 표시
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setInvoiceCurrentPage(p => Math.max(1, p - 1))}
                          disabled={invoiceCurrentPage === 1}
                          data-testid="button-invoice-prev-page"
                        >
                          이전
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalInvoicePages) }, (_, i) => {
                            let pageNum;
                            if (totalInvoicePages <= 5) {
                              pageNum = i + 1;
                            } else if (invoiceCurrentPage <= 3) {
                              pageNum = i + 1;
                            } else if (invoiceCurrentPage >= totalInvoicePages - 2) {
                              pageNum = totalInvoicePages - 4 + i;
                            } else {
                              pageNum = invoiceCurrentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={invoiceCurrentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setInvoiceCurrentPage(pageNum)}
                                data-testid={`button-invoice-page-${pageNum}`}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setInvoiceCurrentPage(p => Math.min(totalInvoicePages, p + 1))}
                          disabled={invoiceCurrentPage === totalInvoicePages}
                          data-testid="button-invoice-next-page"
                        >
                          다음
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
